import { format as formatString, R } from '@/Resources.resx';
import { ProductCardViewModel } from '@/features/programs/model/types';
import { Badge, VerticalContainer, HorizontalContainer } from '@/components';
import { TextTypeIcon, VideoTypeIcon } from '@/components/Icons';
import { ForwardButton } from '@/components/buttons/ForwardButton';

export interface BookConsultationCardProps {
    className?: string;
    productCard: ProductCardViewModel;
    showTags?: boolean;
    disabled?: boolean;
}

export const BookConsultationCard = ({
    productCard,
    className,
    showTags = false,
    disabled = false,
}: BookConsultationCardProps) => {
    const getMinutes = (time: string): number => {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return hours * 60 + minutes + Math.ceil(seconds / 60);
    };

    const cardDto = productCard.CardDto;

    const showNumberOfFreeConsultationsUsed =
        cardDto.ShowNumberOfFreeConsultationsUsed &&
        cardDto.IsFree &&
        (cardDto.GeneralConsultationsAllowed ?? 0) > 0;

    const numberOfFreeConsultationsUsed = showNumberOfFreeConsultationsUsed
        ? formatString(
              R.FreeConsultationsUsed,
              (cardDto.NumberOfActivations ?? 0).toString(),
              (cardDto.GeneralConsultationsAllowed ?? 0).toString(),
          )
        : '';

    const tags = showTags
        ? cardDto.Tags.map((x) => ({
              icon:
                  x.toLowerCase() === R.Video ? (
                      <VideoTypeIcon className="h-7 w-7" />
                  ) : x.toLowerCase() === R.Text ? (
                      <TextTypeIcon className="h-7 w-7" />
                  ) : null,
              text: x,
          }))
        : [];

    const showTopSection =
        (showTags && tags.length > 0) || showNumberOfFreeConsultationsUsed;

    return (
        <a
            href={disabled ? undefined : productCard.Href || ''}
            className={`focus:ring-border-focus-primary group border-border-control-default active:bg-bg-surface hover:bg-bg-subtle bg-bg-surface flex flex-col justify-between rounded-2xl border p-4 transition-colors focus:ring-offset-2 active:ring-2 active:ring-offset-white active:outline-none ${
                disabled ? 'pointer-events-none opacity-60' : ''
            } ${className}`}
        >
            {cardDto.ApproximateWaitingTimeVideo?.IsQueueEnabled && (
                <div>
                    <div className="text-text-primary mb-1 flex items-center gap-2 text-xl font-bold whitespace-normal">
                        <span className="bg-secondary-green-dark h-3 w-3 rounded-full"></span>
                        {cardDto.WaitingTimeText}
                    </div>

                    <div className="text-lg text-gray-900">
                        {formatString(
                            R.HomeApproximateWaitingTime,
                            getMinutes(
                                cardDto.ApproximateWaitingTimeVideo
                                    ?.ApproximateWaitingTime,
                            ).toString(),
                        )}
                    </div>
                </div>
            )}

            {!cardDto.ApproximateWaitingTimeVideo?.IsQueueEnabled && (
                <div>
                    {showTopSection && (
                        <HorizontalContainer className="text-text-primary mb-2 w-full items-center justify-between md:mb-4">
                            <div className="flex flex-row items-center gap-2">
                                {tags.map((tag, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-row items-center gap-2 text-sm font-normal"
                                    >
                                        {tag.icon}
                                        {tag.text}
                                    </div>
                                ))}
                            </div>

                            {showNumberOfFreeConsultationsUsed && (
                                <Badge className="font-normal">
                                    {numberOfFreeConsultationsUsed}
                                </Badge>
                            )}
                        </HorizontalContainer>
                    )}

                    <div className="text-text-heading mb-2 text-xl font-bold whitespace-normal md:mb-4">
                        {cardDto.Header}
                    </div>

                    <div className="text-text-primary text-lg">
                        {cardDto.Description}
                    </div>
                </div>
            )}

            <VerticalContainer spacing="md">
                <div className="border-border-control-default border-b" />

                <HorizontalContainer className="items-end">
                    <VerticalContainer
                        spacing="none"
                        className="justify-center"
                    >
                        {cardDto.ShowPriceInfo && (
                            <>
                                <div className="text-text-primary text-lg">
                                    {cardDto.PriceFormatted}
                                </div>

                                {cardDto.IsFree &&
                                    cardDto.Products[0]?.Price
                                        ?.DefaultPrice && (
                                        <div className="text-text-secondary text-sm">
                                            {formatString(
                                                R.RegularPricePerConsultation,
                                                cardDto.Products[0].Price.DefaultPrice.toString(),
                                            )}
                                        </div>
                                    )}

                                {!cardDto.IsFree && (
                                    <div className="text-text-secondary text-sm">
                                        {R.PerConsultation}
                                    </div>
                                )}
                            </>
                        )}
                    </VerticalContainer>

                    <ForwardButton
                        text={cardDto.ActionButtonText}
                        className="pointer-events-none ml-auto w-auto max-w-[300px]"
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </a>
    );
};
